import { Component, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, Input, Output, EventEmitter, ViewChildren, QueryList, ElementRef, ViewChild, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { VendorsService, SysNotifyService, TenantService, DeviceService } from '@org/shared-services';
import { MediaItem, Sublet, SubletVendorAssignment, VendorTask } from '@org/interfaces';
import { Observable, Subject, from, throwError, of } from 'rxjs';
import { ConfirmationModalTWComponent } from '../confirmation-dialog/confirmation-modal-tw/confirmation-modal-tw.component';
import { debounceTime, distinctUntilChanged, takeUntil, tap, finalize, catchError, map } from 'rxjs/operators';
import { ImageViewerService } from 'libs/shared-services/src/lib/image-viewer/image-viewer.service';

// Interfaces and Types
interface Vendor {
  _id: string;
  name: string;
  phoneNumber: string;
  address?: {
    line1: string;
    city: string;
    state: string;
  };
}

interface ImageUpload {
  file: File | null;
  preview: string;
}

interface VendorWorkFormType {
  requestDetails: FormControl<string | null>;
  roNumber: FormControl<string | null>;
  selectedVendors: FormControl<Vendor[] | null>;
}

interface VendorCategory {
  value: string;
  label: string;
}

interface Tenant {
  _id?: string;
  name: string;
  phoneNumber: string;
  address?: {
    line1: string;
    city: string;
    state: string;
  };
}

// Add type guard
function isTenantValid(tenant: Tenant): tenant is Vendor {
  return typeof tenant._id === 'string' && 
         typeof tenant.name === 'string' && 
         typeof tenant.phoneNumber === 'string';
}

// Add conversion function
function convertToVendor(tenant: Tenant): Vendor | null {
  if (!isTenantValid(tenant)) return null;
  
  return {
    _id: tenant._id,
    name: tenant.name,
    phoneNumber: tenant.phoneNumber,
    address: tenant.address
  };
}

@Component({
    selector: 'lib-vendor-associate',
    imports: [CommonModule, FormsModule, ReactiveFormsModule, ConfirmationModalTWComponent],
    templateUrl: './vendor-associate.component.html',
    styleUrl: './vendor-associate.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VendorAssociateComponent implements OnInit, OnChanges, OnDestroy {
  @Input() taskData: VendorTask | null = null;
  @Input() taskId: string | null = null;
  @Input() subletSelected: SubletVendorAssignment | null = null;
  @Input() taskMedia: MediaItem[] | [] = [];
  @Output() slideOutToggled = new EventEmitter<void>();
  @Output() taskUpdated = new EventEmitter<void>();
  @ViewChildren('fileInput') fileInputs!: QueryList<ElementRef>;
  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;

  // Form and UI state
  vendorWorkForm!: FormGroup<VendorWorkFormType>;
  isLoading = false;
  searchQuery = '';
  selectedVendors: Vendor[] = [];
  imageUploads: ImageUpload[] = Array(3).fill(null).map(() => ({ file: null, preview: '' }));
  vendors: Vendor[] = [];
  selectedVendorCategory = '';
  hasImageChanges = false;
  formHasChanges = false;

  showCameraPreview = false;
  private mediaStream: MediaStream | null = null;
  private currentImageIndex: number = 0;
  private destroy$ = new Subject<void>();
  private vendorCache = new Map<string, Vendor[]>();

  showCameraButton$!: Observable<boolean>;
  imageToDeleteIndex: number | null = null;
  
  // Image viewer state
  showImageViewer = false;
  currentViewingImageIndex = 0;
  isZoomed = false;
  touchStartX = 0;
  touchStartY = 0;
  translateX = 0;
  translateY = 0;
  scale = 1;

  vendorCategories: VendorCategory[] = [
    { value: 'Glass', label: 'Glass' },
    { value: 'Striping', label: 'Striping' },
    { value: 'Sensor', label: 'Sensor' },
    { value: 'Alignment', label: 'Alignment' },
    { value: 'Misc', label: 'Misc' }
  ];

  // Add search debounce
  private searchSubject = new Subject<string>();

  isCompletingRequest = false;
  isCompleteConfirmOpen = false;

  constructor(
    private fb: FormBuilder,
    private vendorsService: VendorsService,
    private tenantService: TenantService,
    private sysNotifyService: SysNotifyService,
    private cdr: ChangeDetectorRef,
    private deviceService: DeviceService,
    private imageViewerService: ImageViewerService
  ) {
    this.showCameraButton$ = this.deviceService.shouldShowCamera$;
  }

  ngOnInit() {
    this.initializeForm();
    this.setupSearch();
    if (this.taskData) {
      this.loadExistingTaskData();
    } else {
      this.loadTaskMedia();
    }
    if (this.subletSelected?.subletName) {
      this.selectedVendorCategory = this.subletSelected.subletName;
      this.onVendorCategoryChange(this.subletSelected.subletName);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.taskData) {
      return;
    }
    if (changes['taskMedia'] || changes['subletSelected']) {
      this.loadTaskMedia();
      if (this.subletSelected?.subletName && !this.selectedVendorCategory) {
        this.selectedVendorCategory = this.subletSelected.subletName;
        this.onVendorCategoryChange(this.subletSelected.subletName);
      }
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.closeCameraPreview();
  }

  private initializeForm() {
    this.vendorWorkForm = this.fb.group<VendorWorkFormType>({
      requestDetails: this.fb.control('', { 
        validators: [Validators.required]
      }),
      roNumber: this.fb.control(''),
      selectedVendors: this.fb.control([], { 
        validators: [Validators.required]
      })
    });

    // Use valueChanges instead of statusChanges
    this.vendorWorkForm.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.formHasChanges = true;
      this.cdr.markForCheck();
    });
  }

  private setupSearch() {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(searchTerm => {
      this.searchQuery = searchTerm;
      this.cdr.markForCheck();
    });
  }

  onSearchInput(value: string): void {
    this.searchQuery = value;
    this.cdr.markForCheck();
  }

  private loadExistingTaskData() {
    if (!this.taskData) return;

    // Set vendor category and load vendors
    if (this.taskData.servicesOffered) {
      this.selectedVendorCategory = this.taskData.servicesOffered;
      this.onVendorCategoryChange(this.taskData.servicesOffered);
    }

    // Set form values
    this.vendorWorkForm.patchValue({
      requestDetails: this.taskData.requestDetails || '',
      roNumber: this.taskData.roNumber || '',
    });

    // Set selected vendors
    if (this.taskData.tenants) {
      const validVendors = this.taskData.tenants
        .map(tenant => convertToVendor(tenant))
        .filter((vendor): vendor is Vendor => vendor !== null);
      this.selectedVendors = validVendors;
      this.vendorWorkForm.patchValue({ selectedVendors: validVendors });
    }

    // Reset and load images if they exist
    this.imageUploads = Array(3).fill(null).map(() => ({ file: null, preview: '' }));
    if (this.taskData.media && this.taskData.media.length > 0) {
      this.hasImageChanges = true;
      this.taskData.media.forEach((media, index) => {
        if (media.imageUrl && index < 3) {
          this.imageUploads[index] = { file: null, preview: media.imageUrl };
        }
      });
    }
  }

  private loadTaskMedia() {
    if (this.taskMedia && this.taskMedia.length > 0) {
      // Reset image uploads first
      this.imageUploads = Array(3).fill(null).map(() => ({ file: null, preview: '' }));
      
      this.taskMedia.forEach(mediaItem => {
        // If we have a selected sublet, only load images for that sublet
        // Otherwise, load all images
        const subletMatches =
          !this.subletSelected ||
          String(mediaItem.subletId?._id ?? '') === String(this.subletSelected?.subletId ?? '');
        if (subletMatches && mediaItem.mediaId?.imageUrl) {
          this.hasImageChanges = true;
          const emptyIndex = this.imageUploads.findIndex(upload => !upload.preview);
          if (emptyIndex !== -1) {
            this.imageUploads[emptyIndex] = { file: null, preview: mediaItem.mediaId.imageUrl };
          }
        }
      });
    }
  }

  onVendorCategoryChange(category: string) {
    this.selectedVendorCategory = category;
    this.searchQuery = '';
    this.selectedVendors = [];
    
    if (!category) return;

    const cached = this.vendorCache.get(category);
    if (cached) {
      this.vendors = cached;
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.tenantService.getAssociatedVendors(category).pipe(
      map((response: any) => {
        // Ensure we have an array of tenants
        const tenants: Tenant[] = Array.isArray(response) ? response : [];
        // Convert and filter valid vendors
        const vendors = tenants
          .map(tenant => convertToVendor(tenant))
          .filter((vendor): vendor is Vendor => vendor !== null);
        return vendors;
      }),
      tap(vendors => {
        this.vendorCache.set(category, vendors);
      }),
      catchError(error => {
        console.error('Error fetching vendors:', error);
        return of([] as Vendor[]);
      }),
      finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (vendors) => {
        this.vendors = vendors;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error in vendor subscription:', error);
        this.showError('Error loading vendors');
      }
    });
  }

  selectVendor(vendor: Vendor) {
    if (!this.selectedVendors.find(v => v._id === vendor._id)) {
      this.selectedVendors = [...this.selectedVendors, vendor];
      this.vendorWorkForm.patchValue({ selectedVendors: this.selectedVendors });
      this.formHasChanges = true;
      this.cdr.markForCheck();
    }
  }

  removeVendor(vendor: Vendor) {
    this.selectedVendors = this.selectedVendors.filter(v => v._id !== vendor._id);
    this.vendorWorkForm.patchValue({ selectedVendors: this.selectedVendors });
    this.formHasChanges = true;
    this.cdr.markForCheck();
  }

  private processImage(file: File, index: number): Observable<string> {
    return new Observable<string>(observer => {
      if (!this.isValidFileType(file) || !this.isValidFileSize(file)) {
        observer.error(new Error('Invalid file'));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        observer.next(e.target?.result as string);
        observer.complete();
      };
      reader.onerror = (error) => observer.error(error);
      reader.readAsDataURL(file);
    }).pipe(
      tap(preview => {
        this.imageUploads[index] = { file, preview };
        this.hasImageChanges = true;
        this.formHasChanges = true;
        this.cdr.markForCheck();
      }),
      catchError(error => {
        console.error('Error processing image:', error);
        return throwError(() => new Error('Failed to process image'));
      })
    );
  }

  onImageUpload(event: Event, index: number) {
    event.preventDefault(); 
    event.stopPropagation(); 

    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    from(this.processImage(file, index)).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      error: () => this.showError('Failed to process image. Please try again.')
    });
  }

  removeImage(event: Event, index: number) {
    event.preventDefault(); 
    event.stopPropagation(); 
    this.imageToDeleteIndex = index;
  }

  onConfirmDelete(){
    if (this.imageToDeleteIndex === null) return;

    const index = this.imageToDeleteIndex;

    this.imageUploads[index].file = null;
    this.imageUploads[index].preview = '';
    this.hasImageChanges = true;
    this.formHasChanges = true; 
    this.vendorWorkForm.markAsDirty();
    this.cdr.markForCheck();

    if (this.taskData?._id && this.taskData?.media[index]?._id) {
      this.vendorsService.deleteImage(this.taskData._id, this.taskData.media[index]._id).subscribe({
        next: () => {
          this.showSuccess('Image removed successfully');
          this.imageToDeleteIndex = null;
          this.closeDeleteConfirmation();
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error deleting image:', error);
          this.showError('Error deleting image');
          this.imageToDeleteIndex = null;
          this.closeDeleteConfirmation();
          this.cdr.markForCheck();
        } 
      });
    } else {
      this.closeDeleteConfirmation();
    }
    
  }

  closeDeleteConfirmation() {
    this.imageToDeleteIndex = null;
    this.cdr.markForCheck();
  }

  private async setupCameraStream(): Promise<MediaStream> {
    const constraints = {
      video: {
        facingMode: 'environment',
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      }
    };

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      return stream;
    } catch (error) {
      console.error('Error accessing camera:', error);
      throw new Error('Camera access denied');
    }
  }

  async captureImage(index: number) {
    if (!this.videoElement?.nativeElement) return;

    try {
      this.currentImageIndex = index;
      this.showCameraPreview = true;
      await this.cdr.detectChanges();
      
      this.mediaStream = await this.setupCameraStream();
      const video = this.videoElement.nativeElement;
      video.srcObject = this.mediaStream;
      await video.play();
    } catch (error) {
      this.showError('Unable to access camera. Please check permissions.');
      this.closeCameraPreview();
    }
  }

  takePhoto() {
    if (!this.videoElement?.nativeElement) return;

    const video = this.videoElement.nativeElement;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      this.showError('Failed to capture photo');
      return;
    }

    // Flip horizontally if using front camera
    context.scale(-1, 1);
    context.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `camera-photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
          from(this.processImage(file, this.currentImageIndex)).pipe(
            takeUntil(this.destroy$)
          ).subscribe({
            next: () => this.closeCameraPreview(),
            error: () => {
              this.showError('Failed to process captured image');
              this.closeCameraPreview();
            }
          });
        } else {
          this.showError('Failed to capture photo');
          this.closeCameraPreview();
        }
      },
      'image/jpeg',
      0.8
    );
  }

  closeCameraPreview() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
    this.showCameraPreview = false;
    this.cdr.markForCheck();
  }

  sendToVendors() {
    const startTime = performance.now();
    if (!this.isSubmitDisabled()) {
      this.isLoading = true;
      const formData = new FormData();
  
      const requestDetails = this.vendorWorkForm.get('requestDetails')?.value;
      const roNumber = this.vendorWorkForm.get('roNumber')?.value;
      
      if (requestDetails) formData.append('requestDetails', requestDetails);
      if (roNumber) formData.append('roNumber', roNumber);
      if (this.taskId) formData.append('taskId', this.taskId);
      
      formData.append('selectedVendors', JSON.stringify(this.selectedVendors));
      formData.append('servicesOffered', this.selectedVendorCategory);
      
      if (this.hasImageChanges) {
        this.imageUploads.forEach((upload, index) => {
          if (upload.file) {
            formData.append(`file${index}`, upload.file);
          }
        });
      }
  
      if (this.taskData) {
        this.vendorsService.updateVendor(formData, this.taskData).subscribe({
          next: () => {
            this.showSuccess('Task updated successfully');
            this.taskUpdated.emit();
            this.toggleVendorSlideOut();
          },
          error: (error) => {
            console.error('Error:', error);
            this.showError('Error updating task');
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        });
      } else {
        this.vendorsService.imageUpload(formData).subscribe({
          next: () => {
            this.showSuccess('Task created successfully');
            this.taskUpdated.emit();
            this.toggleVendorSlideOut();
          },
          error: (error) => {
            console.error('Error:', error);
            this.showError('Error creating task');
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        });
      }
    }
    this.logPerformance('sendToVendors', startTime);
  }
    

  // Helper methods
  private isValidFileType(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    return validTypes.includes(file.type);
  }

  private isValidFileSize(file: File): boolean {
    const maxSize = 10 * 1024 * 1024; // 10MB
    return file.size <= maxSize;
  }

  isFieldInvalid(controlName: string): boolean {
    const control = this.vendorWorkForm.get(controlName);
    return !!control?.errors && control.touched;
  }

  isSubmitDisabled(): boolean {
    return this.isLoading || 
           !this.vendorWorkForm.get('selectedVendors')?.value?.length || 
           !this.vendorWorkForm.get('requestDetails')?.value?.trim() ||
           this.vendorWorkForm.invalid;
  }

  markFormDirty() {
    this.vendorWorkForm.markAsDirty();
  }

  markFormDirtyAndTouched() {
    this.vendorWorkForm.markAsDirty();
    this.vendorWorkForm.markAllAsTouched();
    this.isSubmitDisabled();
    this.cdr.markForCheck();
  }

  getErrorMessage(controlName: string): string {
    const control = this.vendorWorkForm.get(controlName);
    if (!control?.errors || !control.touched) return '';

    switch (controlName) {
      case 'requestDetails':
        if (control.errors['required']) return 'Request details are required';
        if (control.errors['minlength']) return 'Request details must be at least 10 characters';
        break;
      case 'roNumber':
        if (control.errors['required']) return 'RO number is required';
        break;
      case 'selectedVendors':
        if (control.errors['required']) return 'At least one vendor must be selected';
        break;
    }
    return '';
  }

  private resetForm() {
    this.vendorWorkForm.reset();
    this.imageUploads = Array(3).fill(null).map(() => ({ file: null, preview: '' }));
    this.hasImageChanges = false;
    this.formHasChanges = false;
    this.selectedVendors = [];
    this.selectedVendorCategory = '';
    this.cdr.markForCheck();
  }

  toggleVendorSlideOut() {
    this.resetForm();
    this.slideOutToggled.emit();
  }

  triggerFileInput(index: number) {
    const fileInputElements = this.fileInputs.toArray();
    if (fileInputElements[index]) {
      fileInputElements[index].nativeElement.click();
    }
  }

  // Add performance monitoring
  private logPerformance(operation: string, startTime: number) {
    const duration = performance.now() - startTime;
    console.debug(`${operation} took ${duration}ms`);
  }

  showSuccess(message: string) {
    this.sysNotifyService.showSuccess(message, 3000, 'right', 'top');
  }

  showError(message: string) {
    this.sysNotifyService.showError(message, 3000, 'right', 'top');
  }

  // Add trackBy function for vendor lists
  trackByVendor(index: number, vendor: Vendor): string {
    return vendor._id;
  }

  // Add method to filter vendors
  getFilteredVendors(): Vendor[] {
    if (!this.searchQuery) return this.vendors;
    
    const searchTerm = this.searchQuery.toLowerCase();
    return this.vendors.filter(vendor => 
      vendor.name.toLowerCase().includes(searchTerm) ||
      vendor.phoneNumber.includes(searchTerm) ||
      vendor.address?.city.toLowerCase().includes(searchTerm) ||
      vendor.address?.state.toLowerCase().includes(searchTerm)
    );
  }

  isVendorSelected(vendor: Vendor): boolean {
    return this.selectedVendors.some(v => v._id === vendor._id);
  }

  toggleVendorSelection(vendor: Vendor): void {
    if (this.isVendorSelected(vendor)) {
      this.removeVendor(vendor);
    } else {
      this.selectVendor(vendor);
    }
  }

  getSelectedVendorNames(): string {
    return this.selectedVendors
      .map(v => v.name)
      .join(', ');
  }

  openImageViewer(slotIndex: number, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const previewsWithSlots = this.imageUploads
      .map((upload, i) => ({ upload, slotIndex: i }))
      .filter(({ upload }) => !!upload.preview);
    const viewerIndex = previewsWithSlots.findIndex(({ slotIndex: i }) => i === slotIndex);
    const images = previewsWithSlots.map(({ upload }) => upload.preview);
    if (images.length > 0 && viewerIndex >= 0) {
      this.imageViewerService.open(images, viewerIndex);
    }
  }

  openCompleteConfirm() {
    this.isCompleteConfirmOpen = true;
    this.cdr.markForCheck();
  }

  onConfirmCompleteRequest() {
    this.isCompleteConfirmOpen = false;
    this.completeRequest();
  }

  onCancelCompleteRequest() {
    this.isCompleteConfirmOpen = false;
    this.cdr.markForCheck();
  }

  completeRequest() {
    if (!this.taskData?._id) return;
    this.isCompletingRequest = true;
    this.vendorsService.completeVendorTaskByAdmin(this.taskData._id).subscribe({
      next: () => {
        this.showSuccess('Request marked as complete');
        this.taskUpdated.emit();
        this.toggleVendorSlideOut();
        this.isCompletingRequest = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error completing request:', error);
        this.showError('Failed to complete request');
        this.isCompletingRequest = false;
        this.cdr.markForCheck();
      }
    });
  }
}